import { memo, useEffect, useMemo, useRef, useState } from "react";
import { ThemeType } from "@markit/common.types";
import { useTheme } from "../../hooks/useTheme";
import NumberInput from "../NumberInput";
import RectangleButton from "../Buttons/RectangleButton";
import { Colors } from "../../utils/colors";
import { FormatCurrency } from "../../utils/FormatCurrency";
import ExpandableBox from "../Containers/ExpandableBox";

const TIP_PRESET_PERCENTS = [15, 20];
const MAX_TIP_CENTS = 100000; // $1,000

// Which pill is currently selected - a percent preset stays pinned to that
// percent as the ticket subtotal changes; "none" and "custom" hold a fixed
// dollar amount regardless of subtotal changes.
type TipMode = "none" | "custom" | (typeof TIP_PRESET_PERCENTS)[number];

type FullEventTipInputProps = {
  ticketSubtotalCents: number;
  tipAmountCents: number;
  setTipAmountCents: (tipAmountCents: number) => void;
  hardTheme?: ThemeType;
};

// Lets a buyer add a tip via preset percentage buttons off the ticket subtotal, or a custom dollar amount.
const FullEventTipInput = memo(function FullEventTipInputFn(
  props: FullEventTipInputProps
) {
  const { ticketSubtotalCents, tipAmountCents, setTipAmountCents, hardTheme } =
    props;
  const { theme } = useTheme(hardTheme);

  const styles = {
    pillSelected: {
      flex: 1,
      backgroundColor: Colors.BLUE5,
      color: Colors.WHITE,
      border: `1px solid ${Colors.BLUE5}`,
      height: 40,
    },
    pillUnselected: {
      flex: 1,
      backgroundColor: theme.NewPrimaryBG.backgroundColor,
      color: theme.PrimaryText.color,
      border: `1px solid ${theme.EditableTextFieldBorder.borderColor}`,
      height: 40,
    },
    pillValue: { display: "block", fontSize: 10 },
    inputContainer: { padding: "8px 14px", borderRadius: 12, width: 100 },
  };

  const presetCentsByPercent = useMemo(
    () =>
      new Map(
        TIP_PRESET_PERCENTS.map((percent) => [
          percent,
          Math.round((ticketSubtotalCents * percent) / 100),
        ])
      ),
    [ticketSubtotalCents]
  );

  // Derive the initial mode from the incoming tip amount (e.g. a preset percent applied
  // before this component mounted) instead of always defaulting to "none".
  const [mode, setMode] = useState<TipMode>(() => {
    if (tipAmountCents === 0) return "none";
    const matchedPercent = TIP_PRESET_PERCENTS.find(
      (percent) =>
        Math.round((ticketSubtotalCents * percent) / 100) === tipAmountCents
    );
    return matchedPercent ?? "custom";
  });

  // Tracked separately from tipAmountCents so the field can sit empty (rather than
  // snapping back to "0") while the buyer is backspacing/typing a new amount.
  const [customDisplayValue, setCustomDisplayValue] = useState<
    number | undefined
  >(() => (tipAmountCents > 0 ? tipAmountCents / 100 : undefined));

  // When the ticket subtotal changes (e.g. flexible pricing amount, quantity), a percent
  // preset recalculates its dollar amount off the new subtotal; "none"/"custom" hold steady.
  const prevSubtotalRef = useRef(ticketSubtotalCents);
  useEffect(() => {
    if (
      typeof mode === "number" &&
      prevSubtotalRef.current !== ticketSubtotalCents
    ) {
      setTipAmountCents(Math.round((ticketSubtotalCents * mode) / 100));
    }
    prevSubtotalRef.current = ticketSubtotalCents;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketSubtotalCents]);

  return (
    <ExpandableBox>
      <div className="ColumnNormal" style={{ gap: 10 }}>
        <div className="ColumnNormal" style={{ gap: 4 }}>
          <span className="bodyMedium" style={theme.PrimaryText}>
            Add a Tip
          </span>
          <span className="smallBodySubtext" style={{ color: Colors.GRAY1 }}>
            Show your support or appreciation!
          </span>
        </div>
        <div className="AlignedRow" style={{ gap: 7 }}>
          <RectangleButton
            buttonLabel={
              <div className="Centering" style={{ width: "100%" }}>
                <span
                  className="bodyMedium"
                  style={{
                    fontWeight: 600,
                    color:
                      mode === "none" ? Colors.WHITE : theme.PrimaryText.color,
                  }}
                >
                  No Tip
                </span>
              </div>
            }
            onPress={() => {
              setMode("none");
              setTipAmountCents(0);
            }}
            altPaddingVert={8}
            altPaddingHorz={4}
            containerStyles={
              mode === "none" ? styles.pillSelected : styles.pillUnselected
            }
          />
          {TIP_PRESET_PERCENTS.map((percent) => {
            const presetCents = presetCentsByPercent.get(percent) ?? 0;
            const isSelected = mode === percent;
            return (
              <RectangleButton
                key={percent}
                buttonLabel={
                  <div className="ColumnNormal">
                    <span
                      style={{
                        color: isSelected
                          ? Colors.WHITE
                          : theme.PrimaryText.color,
                      }}
                    >
                      {percent}%
                    </span>
                    <span
                      style={{
                        ...styles.pillValue,
                        color: isSelected ? Colors.WHITE : Colors.GRAY1,
                      }}
                    >
                      {FormatCurrency(presetCents / 100)}
                    </span>
                  </div>
                }
                onPress={() => {
                  setMode(percent);
                  setTipAmountCents(presetCents);
                }}
                altPaddingVert={8}
                altPaddingHorz={4}
                containerStyles={
                  isSelected ? styles.pillSelected : styles.pillUnselected
                }
              />
            );
          })}
          <RectangleButton
            buttonLabel={
              <div className="Centering" style={{ width: "100%" }}>
                <span
                  className="bodyMedium"
                  style={{
                    fontWeight: 600,
                    color:
                      mode === "custom"
                        ? Colors.WHITE
                        : theme.PrimaryText.color,
                  }}
                >
                  Custom
                </span>
              </div>
            }
            onPress={() => {
              setMode("custom");
              setCustomDisplayValue(tipAmountCents / 100);
            }}
            altPaddingVert={8}
            altPaddingHorz={4}
            containerStyles={
              mode === "custom" ? styles.pillSelected : styles.pillUnselected
            }
          />
        </div>
        {mode === "custom" ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <NumberInput
              min={0}
              max={MAX_TIP_CENTS / 100}
              prefix="$"
              value={customDisplayValue}
              onChange={(value) => {
                setMode("custom");
                setCustomDisplayValue(value);
                setTipAmountCents(value ? Math.round(value * 100) : 0);
              }}
              containerStyles={styles.inputContainer}
              allowDecimal
              suppressErrorMessage
              hardTheme={hardTheme}
            />
          </div>
        ) : null}
      </div>
    </ExpandableBox>
  );
});

export default FullEventTipInput;
